import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Lock, Plus, ShieldAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { SG_STATUS_COLORS, SG_STATUS_LABELS, SAFEGUARDING_CATEGORY_LABELS } from "@/config/incidentCatalog";

export default function SafeguardingPage() {
  const { hasPermission } = useAuth();
  const canAccess = hasPermission("safeguarding:access");
  const [dash, setDash] = useState(null);
  const [items, setItems] = useState(null);
  const [mine, setMine] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    if (canAccess) {
      api.get("/safeguarding/dashboard").then((r) => setDash(r.data)).catch((e) => toast.error(formatApiError(e)));
    } else {
      api.get("/safeguarding/mine").then((r) => setMine(r.data.items)).catch(() => setMine([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess]);

  useEffect(() => {
    if (!canAccess) return;
    const params = statusFilter ? { status: statusFilter } : {};
    api.get("/safeguarding", { params }).then((r) => setItems(r.data.items)).catch((e) => toast.error(formatApiError(e)));
  }, [canAccess, statusFilter]);

  return (
    <div className="space-y-4" data-testid="safeguarding-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Lock className="h-6 w-6 text-red-600" /> Safeguarding
          </h1>
          <p className="text-sm text-slate-600 mt-1">Restricted module. All access is recorded in the audit trail.</p>
        </div>
        <Link to="/safeguarding/new" data-testid="sg-raise-button"
          className="inline-flex h-11 items-center gap-2 rounded-md bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 transition-colors duration-200">
          <Plus className="h-4 w-4" /> Raise safeguarding concern
        </Link>
      </div>

      {canAccess ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="sg-dashboard">
            {[
              ["new", "New concerns"], ["urgent", "Urgent reviews"], ["open", "Open concerns"],
              ["external_referrals", "External referrals"], ["closed", "Closed"],
            ].map(([k, label]) => (
              <Card key={k} data-testid={`sg-stat-${k}`}>
                <CardContent className="p-4">
                  <p className="text-2xl font-bold text-slate-900">{dash ? dash[k] : "…"}</p>
                  <p className="text-xs text-slate-500">{label}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-lg">Safeguarding records ({items?.length ?? "…"})</CardTitle>
              <select data-testid="sg-filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="h-11 rounded-md border border-border bg-white px-3 text-sm">
                <option value="">All statuses</option>
                {Object.entries(SG_STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </CardHeader>
            <CardContent>
              {items === null ? (
                <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
              ) : items.length === 0 ? (
                <p className="text-sm text-slate-600" data-testid="sg-empty">No safeguarding records match.</p>
              ) : (
                <div className="space-y-2">
                  {items.map((s) => (
                    <Link key={s.id} to={`/safeguarding/${s.id}`} data-testid={`sg-row-${s.sg_number}`}
                      className="flex flex-wrap items-center gap-3 rounded-md border border-border p-3 hover:bg-muted transition-colors duration-200">
                      <div className="min-w-[140px]">
                        <p className="font-mono font-semibold text-slate-900">{s.sg_number}</p>
                        <p className="text-xs text-slate-500">{s.child?.case_id} · {s.identified_date}</p>
                      </div>
                      <p className="text-xs text-slate-600 flex-1">{SAFEGUARDING_CATEGORY_LABELS[s.category] || s.category} · reported by {s.reporter_name}</p>
                      <Badge className={SG_STATUS_COLORS[s.status]}>{SG_STATUS_LABELS[s.status]}</Badge>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <Card data-testid="sg-mine">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-red-600" /> Concerns you have raised</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-3">
              For confidentiality, ongoing safeguarding case management is restricted to authorised safeguarding staff.
              You can raise a concern for any child you legitimately work with.
            </p>
            {mine === null ? (
              <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : mine.length === 0 ? (
              <p className="text-sm text-slate-600" data-testid="sg-mine-empty">You have not raised any safeguarding concerns.</p>
            ) : (
              <div className="space-y-2">
                {mine.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 rounded-md border border-border p-3" data-testid={`sg-mine-${s.sg_number}`}>
                    <span className="font-mono font-semibold text-slate-900">{s.sg_number}</span>
                    <span className="text-xs text-slate-500">identified {s.identified_date}</span>
                    <Badge className={`${SG_STATUS_COLORS[s.status]} ml-auto`}>{SG_STATUS_LABELS[s.status]}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
